import * as stylex from "@stylexjs/stylex";
import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "@/design/tokens.stylex";
import { colors, radii, shadows } from "@/design/tokens.stylex";
import { placeHoverCard } from "./hover-card-placement";

/** Hover waits this long so a pointer crossing the table does not flash cards. */
const OPEN_DELAY_MS = 250;
/** Leaving waits a little, so moving from the trigger into the card keeps it open. */
const CLOSE_DELAY_MS = 120;
const VIEWPORT_MARGIN = 8;
interface Position {
  top: number;
  left: number;
}

/**
 * A preview that opens beside its trigger on hover or keyboard focus and
 * closes on leave, blur, or Escape. Rendered in a portal at a fixed position,
 * so it escapes the table cells that clip their overflow. The card itself can
 * be hovered, so its content may hold buttons. Purely a peek: nothing inside
 * is reachable by keyboard, so anything actionable must also exist elsewhere.
 */
export default function HoverCard({
  content,
  onOpen,
  children
}: {
  /** Rendered only while open; null closes nothing but shows nothing. */
  content: ReactNode;
  /** Runs once each time the card opens, e.g. to start loading what it shows. */
  onOpen?: () => void;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<Position | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const id = useId();
  const clearTimer = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }, []);
  const scheduleOpen = useCallback((delay: number) => {
    clearTimer();
    timer.current = setTimeout(() => setOpen(true), delay);
  }, [clearTimer]);
  const scheduleClose = useCallback(() => {
    clearTimer();
    timer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  }, [clearTimer]);
  useEffect(() => clearTimer, [clearTimer]);
  useEffect(() => {
    if (!open) return;
    onOpen?.();
    // Only depends on the open flag: a new onOpen identity must not reopen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Placed where it fits (see placeHoverCard), and placed again whenever the
  // card changes size, e.g. once a peek learns its media's aspect ratio.
  useEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    const trigger = triggerRef.current;
    const card = cardRef.current;
    if (!trigger || !card) return;
    const place = () => {
      const anchor = trigger.getBoundingClientRect();
      const size = card.getBoundingClientRect();
      setPosition(placeHoverCard({
        top: anchor.top,
        left: anchor.left,
        width: anchor.width,
        height: anchor.height
      }, {
        width: size.width,
        height: size.height
      }, {
        width: window.innerWidth,
        height: window.innerHeight
      }, VIEWPORT_MARGIN));
    };
    place();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(place);
    observer.observe(card);
    return () => observer.disconnect();
  }, [open, content]);
  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    // Scrolling the table moves the trigger; closing is simpler than tracking.
    function onScroll() {
      setOpen(false);
    }
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);
  return <>
      <span ref={triggerRef} aria-describedby={open ? id : undefined} onPointerEnter={event => {
      if (event.pointerType === "touch") return;
      scheduleOpen(OPEN_DELAY_MS);
    }} onPointerLeave={scheduleClose} onFocus={() => {
      clearTimer();
      setOpen(true);
    }} onBlur={scheduleClose} className="x3nfvp2 xeuugli">
        {children}
      </span>
      {open && content !== null ? createPortal(<div ref={cardRef} id={id} role="tooltip" onPointerEnter={clearTimer} onPointerLeave={scheduleClose} style={position ? {
      top: position.top,
      left: position.left
    } : {
      top: 0,
      left: 0,
      visibility: "hidden"
    }} className="xixxii4 xf5e64p xe8ttls xmkeg23 x1y0btm7 xzbo3x7 x16l1o21 x1h1f4i9 x1dgl1gp">
              {content}
            </div>, document.body) : null}
    </>;
}